import { detectDelimiter, splitCsvLine, splitCsvLineWithFallback, type CsvDelimiter } from './csvParsing'

export type ParsedImportSource = {
  format: 'csv'
  headerLine: string
  delimiter: CsvDelimiter
  fallbackDelimiter: CsvDelimiter
  expectedColumns: number
}

export type ParsedImportRow = {
  sourceRowNumber: number
  rawLine: string
  values: string[]
}

export type ParsedImportTable = {
  source: ParsedImportSource
  headers: string[]
  rows: ParsedImportRow[]
}

export type ImportMappedHeader<TField extends string = string> = {
  sourceHeader: string
  normalizedHeader: string
  targetField: TField | null
}

export type ImportPreviewCell<TField extends string = string> = {
  sourceHeader: string
  normalizedHeader: string
  targetField: TField | null
  value: string
}

export type MappedImportPreviewCell<TField extends string = string> = ImportPreviewCell<TField> & {
  targetField: TField
}

export type ImportPreviewRow<TField extends string = string> = {
  sourceRowNumber: number
  rawLine: string
  cells: Array<ImportPreviewCell<TField>>
}

export type ImportIssueSeverity = 'info' | 'warning' | 'error'

export type ImportRowIssue<TField extends string = string> = {
  sourceRowNumber: number
  code: string
  severity: ImportIssueSeverity
  message: string
  sourceHeader?: string
  normalizedHeader?: string
  targetField?: TField
}

export type ImportRowDedupeReason = 'accepted' | 'missing' | 'existing' | 'duplicate'

export type ImportRowDedupeResult = {
  requestedId: string
  finalId: string
  reason: ImportRowDedupeReason
}

export type ImportValidationResult<TField extends string = string> = {
  previewRow: ImportPreviewRow<TField>
  record: Record<string, unknown>
  issues: Array<ImportRowIssue<TField>>
}

export type ImportRecordResult<
  TRecord extends object,
  TField extends string = string
> = {
  previewRow: ImportPreviewRow<TField>
  record: TRecord
  issues: Array<ImportRowIssue<TField>>
  dedupeResult: ImportRowDedupeResult | null
}

export type ImportResultOutcome = 'ready' | 'created' | 'updated' | 'skipped'

export type ImportIssueSummary<TField extends string = string> = {
  code: string
  severity: ImportIssueSeverity
  targetField?: TField
  count: number
  rowNumbers: number[]
  sourceHeaders: string[]
  messages: string[]
}

export type ImportResultSummary<TField extends string = string> = {
  totalRows: number
  validRows: number
  invalidRows: number
  rowsWithWarnings: number
  rowsWithErrors: number
  totalIssues: number
  issueCountsBySeverity: Record<ImportIssueSeverity, number>
  issueSummaries: Array<ImportIssueSummary<TField>>
  dedupeCounts: Record<ImportRowDedupeReason, number>
  regeneratedIdCount: number
  outcomeCounts: Record<ImportResultOutcome, number>
  createdOrSkippedSummary: {
    created: number
    skipped: number
  }
}

export type ImportSourceFile = {
  name: string
  format: ParsedImportSource['format']
  sizeBytes?: number
  mimeType?: string
  lastModifiedMs?: number
}

export type ImportSessionTarget<TTarget extends string = string> = {
  targetKey: TTarget
  label?: string
}

export type ImportSession<
  TRecord extends object,
  TField extends string = string,
  TTarget extends string = string,
  TContext extends Record<string, string> = Record<string, string>
> = {
  sourceFile: ImportSourceFile | null
  table: ParsedImportTable
  selectedTarget: ImportSessionTarget<TTarget> | null
  assignedContext: TContext | null
  mappedHeaders: Array<ImportMappedHeader<TField>>
  results: Array<ImportRecordResult<TRecord, TField>>
  summary: ImportResultSummary<TField>
}

const splitImportLines = (text: string): string[] => {
  return (text ?? '').split(/\r?\n/)
}

// A CSV line is "blank" when it contains no content beyond whitespace and the
// common cell delimiters (',' ';' '\t'). This catches both truly empty lines
// and padding rows like ",,,," that some spreadsheet programs export above or
// below the real data table.
const isBlankCsvLine = (line: string): boolean => {
  return line.replace(/[\s,;\t]/g, '') === ''
}

export const parseCsvImportTable = (text: string): ParsedImportTable | null => {
  const allLines = splitImportLines(text)
  let headerIndex = -1
  for (let i = 0; i < allLines.length; i += 1) {
    if (!isBlankCsvLine(allLines[i])) {
      headerIndex = i
      break
    }
  }
  if (headerIndex < 0) return null

  const headerLine = allLines[headerIndex]
  const delimiter = detectDelimiter(headerLine)
  const fallbackDelimiter = delimiter === ',' ? ';' : ','
  const headers = splitCsvLine(headerLine, delimiter).map((value) => value.trim())
  const expectedColumns = headers.length

  const rows: ParsedImportRow[] = []
  for (let i = headerIndex + 1; i < allLines.length; i += 1) {
    const rawLine = allLines[i]
    if (isBlankCsvLine(rawLine)) continue
    rows.push({
      sourceRowNumber: i + 1,
      rawLine,
      values: splitCsvLineWithFallback(rawLine, delimiter, fallbackDelimiter, expectedColumns)
    })
  }

  return {
    source: {
      format: 'csv',
      headerLine,
      delimiter,
      fallbackDelimiter,
      expectedColumns
    },
    headers,
    rows
  }
}

export const replaceImportTableRowRawLine = (args: {
  table: ParsedImportTable
  sourceRowNumber: number
  nextRawLine: string
}): string => {
  return replaceImportTableRowRawLines({
    table: args.table,
    replacements: [{
      sourceRowNumber: args.sourceRowNumber,
      nextRawLine: args.nextRawLine
    }]
  })
}

export const replaceImportTableRowRawLines = (args: {
  table: ParsedImportTable
  replacements: Array<{
    sourceRowNumber: number
    nextRawLine: string
  }>
}): string => {
  const replacementsByRowNumber = new Map<number, string>()
  args.replacements.forEach((replacement) => {
    replacementsByRowNumber.set(replacement.sourceRowNumber, String(replacement.nextRawLine ?? ''))
  })

  const nextRows = args.table.rows.map((row) => {
    return replacementsByRowNumber.get(row.sourceRowNumber) ?? row.rawLine
  })

  return [args.table.source.headerLine, ...nextRows].join('\n')
}

export const mapImportHeaders = <TField extends string>(args: {
  headers: string[]
  normalizeHeader: (value: string) => string
  targetByNormalizedHeader: Partial<Record<string, TField>>
}): Array<ImportMappedHeader<TField>> => {
  return args.headers.map((sourceHeader) => {
    const normalizedHeader = args.normalizeHeader(sourceHeader)
    return {
      sourceHeader,
      normalizedHeader,
      targetField: args.targetByNormalizedHeader[normalizedHeader] ?? null
    }
  })
}

export const buildImportPreviewRows = <TField extends string>(args: {
  table: ParsedImportTable
  mappedHeaders: ReadonlyArray<ImportMappedHeader<TField>>
  normalizeCellValue?: (value: string) => string
}): Array<ImportPreviewRow<TField>> => {
  return args.table.rows.map((row) => ({
    sourceRowNumber: row.sourceRowNumber,
    rawLine: row.rawLine,
    cells: args.mappedHeaders.map((mappedHeader, index) => ({
      sourceHeader: mappedHeader.sourceHeader,
      normalizedHeader: mappedHeader.normalizedHeader,
      targetField: mappedHeader.targetField,
      value: args.normalizeCellValue
        ? args.normalizeCellValue(row.values[index] ?? '')
        : row.values[index] ?? ''
    }))
  }))
}

export const buildImportPreviewRecord = <TField extends string>(args: {
  previewRow: ImportPreviewRow<TField>
  mapCellValue?: (cell: MappedImportPreviewCell<TField>) => unknown
}): Record<string, unknown> => {
  const row: Record<string, unknown> = {}

  args.previewRow.cells.forEach((cell) => {
    if (!cell.targetField) return
    const mappedCell: MappedImportPreviewCell<TField> = {
      ...cell,
      targetField: cell.targetField
    }
    row[mappedCell.targetField] = args.mapCellValue
      ? args.mapCellValue(mappedCell)
      : mappedCell.value
  })

  return row
}

export const createImportRowIssue = <TField extends string>(args: {
  previewRow: ImportPreviewRow<TField>
  code: string
  severity: ImportIssueSeverity
  message: string
  targetField?: TField
}): ImportRowIssue<TField> => {
  const matchedCell = args.targetField
    ? args.previewRow.cells.find((cell) => cell.targetField === args.targetField)
    : undefined

  return {
    sourceRowNumber: args.previewRow.sourceRowNumber,
    code: args.code,
    severity: args.severity,
    message: args.message,
    sourceHeader: matchedCell?.sourceHeader,
    normalizedHeader: matchedCell?.normalizedHeader,
    targetField: args.targetField
  }
}

export const buildImportValidationResults = <TField extends string>(args: {
  previewRows: Array<ImportPreviewRow<TField>>
  buildRecord: (previewRow: ImportPreviewRow<TField>) => Record<string, unknown>
  validateRecord?: (
    result: Omit<ImportValidationResult<TField>, 'issues'>
  ) => Array<ImportRowIssue<TField>>
}): Array<ImportValidationResult<TField>> => {
  return args.previewRows.map((previewRow) => {
    const baseResult = {
      previewRow,
      record: args.buildRecord(previewRow)
    }

    return {
      ...baseResult,
      issues: args.validateRecord ? args.validateRecord(baseResult) : []
    }
  })
}

export const createImportDedupeIssue = <TField extends string>(args: {
  previewRow: ImportPreviewRow<TField>
  targetField: TField
  dedupeResult: ImportRowDedupeResult
}): ImportRowIssue<TField> | null => {
  if (args.dedupeResult.reason === 'accepted') return null

  if (args.dedupeResult.reason === 'missing') {
    return createImportRowIssue({
      previewRow: args.previewRow,
      targetField: args.targetField,
      code: 'import-id-missing',
      severity: 'warning',
      message: 'Generated a new import id because the source row did not include a usable id.'
    })
  }

  if (args.dedupeResult.reason === 'existing') {
    return createImportRowIssue({
      previewRow: args.previewRow,
      targetField: args.targetField,
      code: 'import-id-conflicts-existing',
      severity: 'warning',
      message: `Generated a new import id because "${args.dedupeResult.requestedId}" already exists.`
    })
  }

  return createImportRowIssue({
    previewRow: args.previewRow,
    targetField: args.targetField,
    code: 'import-id-duplicate-in-file',
    severity: 'warning',
    message: `Generated a new import id because "${args.dedupeResult.requestedId}" was duplicated in the import file.`
  })
}

const compareIssueSeverity = (left: ImportIssueSeverity, right: ImportIssueSeverity): number => {
  const rank: Record<ImportIssueSeverity, number> = {
    error: 0,
    warning: 1,
    info: 2
  }
  return rank[left] - rank[right]
}

export const summarizeImportResults = <
  TRecord extends object,
  TField extends string = string
>(args: {
  results: Array<ImportRecordResult<TRecord, TField>>
  resolveOutcome?: (result: ImportRecordResult<TRecord, TField>) => ImportResultOutcome
}): ImportResultSummary<TField> => {
  const resolveOutcome = args.resolveOutcome ?? ((result: ImportRecordResult<TRecord, TField>) => {
    return result.issues.some((issue) => issue.severity === 'error') ? 'skipped' : 'ready'
  })

  const issueCountsBySeverity: Record<ImportIssueSeverity, number> = {
    info: 0,
    warning: 0,
    error: 0
  }
  const dedupeCounts: Record<ImportRowDedupeReason, number> = {
    accepted: 0,
    missing: 0,
    existing: 0,
    duplicate: 0
  }
  const outcomeCounts: Record<ImportResultOutcome, number> = {
    ready: 0,
    created: 0,
    updated: 0,
    skipped: 0
  }

  const issueSummaries = new Map<string, ImportIssueSummary<TField>>()

  let totalIssues = 0
  let validRows = 0
  let invalidRows = 0
  let rowsWithWarnings = 0
  let rowsWithErrors = 0

  args.results.forEach((result) => {
    const hasWarnings = result.issues.some((issue) => issue.severity === 'warning')
    const hasErrors = result.issues.some((issue) => issue.severity === 'error')

    if (hasWarnings) rowsWithWarnings += 1
    if (hasErrors) rowsWithErrors += 1

    if (hasErrors) invalidRows += 1
    else validRows += 1

    const outcome = resolveOutcome(result)
    outcomeCounts[outcome] += 1

    if (result.dedupeResult) {
      dedupeCounts[result.dedupeResult.reason] += 1
    }

    result.issues.forEach((issue) => {
      totalIssues += 1
      issueCountsBySeverity[issue.severity] += 1

      const key = `${issue.code}::${issue.severity}::${issue.targetField ?? ''}`
      const existing = issueSummaries.get(key)

      if (!existing) {
        issueSummaries.set(key, {
          code: issue.code,
          severity: issue.severity,
          targetField: issue.targetField,
          count: 1,
          rowNumbers: [issue.sourceRowNumber],
          sourceHeaders: issue.sourceHeader ? [issue.sourceHeader] : [],
          messages: [issue.message]
        })
        return
      }

      existing.count += 1
      if (!existing.rowNumbers.includes(issue.sourceRowNumber)) {
        existing.rowNumbers.push(issue.sourceRowNumber)
      }
      if (issue.sourceHeader && !existing.sourceHeaders.includes(issue.sourceHeader)) {
        existing.sourceHeaders.push(issue.sourceHeader)
      }
      if (!existing.messages.includes(issue.message)) {
        existing.messages.push(issue.message)
      }
    })
  })

  return {
    totalRows: args.results.length,
    validRows,
    invalidRows,
    rowsWithWarnings,
    rowsWithErrors,
    totalIssues,
    issueCountsBySeverity,
    issueSummaries: Array.from(issueSummaries.values()).sort((left, right) => {
      const severityComparison = compareIssueSeverity(left.severity, right.severity)
      if (severityComparison !== 0) return severityComparison
      if (left.count !== right.count) return right.count - left.count
      return left.code.localeCompare(right.code)
    }),
    dedupeCounts,
    regeneratedIdCount: dedupeCounts.missing + dedupeCounts.existing + dedupeCounts.duplicate,
    outcomeCounts,
    createdOrSkippedSummary: {
      created: outcomeCounts.created,
      skipped: outcomeCounts.skipped
    }
  }
}

export const buildImportSession = <
  TRecord extends object,
  TField extends string = string,
  TTarget extends string = string,
  TContext extends Record<string, string> = Record<string, string>
>(args: {
  sourceFile?: ImportSourceFile | null
  table: ParsedImportTable
  selectedTarget?: ImportSessionTarget<TTarget> | null
  assignedContext?: TContext | null
  mappedHeaders: Array<ImportMappedHeader<TField>>
  results: Array<ImportRecordResult<TRecord, TField>>
  resolveOutcome?: (result: ImportRecordResult<TRecord, TField>) => ImportResultOutcome
}): ImportSession<TRecord, TField, TTarget, TContext> => {
  return {
    sourceFile: args.sourceFile ?? null,
    table: args.table,
    selectedTarget: args.selectedTarget ?? null,
    assignedContext: args.assignedContext ?? null,
    mappedHeaders: args.mappedHeaders.slice(),
    results: args.results.slice(),
    summary: summarizeImportResults({
      results: args.results,
      resolveOutcome: args.resolveOutcome
    })
  }
}